import Highcharts from "highcharts";

/**
 * Gera a referência de uma célula no estilo Excel (ex.: "A1", "B2", "AA10").
 *
 * @param {number} colIndex - Índice da coluna (0-based).
 * @param {number} rowIndex - Número da linha (1-based).
 * @returns {string} Identificador da célula em formato Excel.
 */
export const toCellString = (colIndex, rowIndex) => {
  let col1Based = colIndex + 1;

  let col = "";
  while (col1Based >= 0) {
    col = String.fromCharCode((col1Based % 26) + 65) + col;
    col1Based = Math.floor(col1Based / 26) - 1;
  }

  return `${col}${rowIndex}`;
};

export const range = (start, end) => {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const getNegativeRemainingMonths = (
  data,
  rows,
  dateRange,
  executedDate,
  hotInstance = null,
) => {
  if (!Array.isArray(data) || !Array.isArray(dateRange)) return [];
  const remainingRowIndex = rows?.remainingHours;
  if (!Number.isFinite(remainingRowIndex)) return [];

  const rowIndex0 = remainingRowIndex - 1;
  const row = Array.isArray(data) ? data[rowIndex0] : null;
  if (!row && !hotInstance) return [];

  const parseNumber = (value) => {
    if (value == null || value === "") return 0;
    if (typeof value === "number") {
      return Number.isNaN(value) ? 0 : value;
    }
    if (typeof value === "string") {
      const normalized = value.replace(/\s/g, "").replace(",", ".");
      const num = Number(normalized);
      return Number.isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const readValue = (month) => {
    if (hotInstance) {
      if (
        typeof hotInstance.propToCol === "function" &&
        typeof hotInstance.getDataAtCell === "function"
      ) {
        const colIndex = hotInstance.propToCol(month);
        if (Number.isFinite(colIndex)) {
          return hotInstance.getDataAtCell(rowIndex0, colIndex);
        }
      }
      if (typeof hotInstance.getDataAtRowProp === "function") {
        return hotInstance.getDataAtRowProp(rowIndex0, month);
      }
    }
    return row?.[month];
  };

  const collectNegatives = (months) =>
    months.filter((month) => parseNumber(readValue(month)) < 0);

  if (executedDate) {
    const afterExecution = dateRange.filter((month) => month > executedDate);
    const negativesAfterExecution = collectNegatives(afterExecution);
    if (negativesAfterExecution.length) return negativesAfterExecution;
  }

  return collectNegatives(dateRange);
};

// Real é desenhado ligeiramente acima do Previsto (em vez de centrado),
// para que fique sempre visível a espreitar por cima, sem duplicar para baixo.
const PREVISTO_POINT_WIDTH = 14;
const REAL_POINT_WIDTH = 14;
const REAL_Y_OFFSET = 0.22;

const processActivity = (activity, y, color, isReal = false) => {
  const startKey = isReal ? "real_start_date" : "start_date";
  const endKey = isReal ? "real_end_date" : "end_date";
  const interruptionKey = isReal ? "real_interruptions" : "interruptions";

  const startDate = activity[startKey]
    ? new Date(activity[startKey]).getTime()
    : null;
  const endDate = activity[endKey]
    ? new Date(activity[endKey]).getTime()
    : null;

  if (isNaN(startDate) || isNaN(endDate)) return [];

  let segments = [];

  const INTERVAL_RE = /^\s*(\d{4})-(\d{2})\s*:\s*(\d{4})-(\d{2})\s*$/;

  const interruptions =
    activity[interruptionKey]
      ?.trim()
      .split(";")
      .filter(Boolean)
      .map((i) => {
        const m = INTERVAL_RE.exec(i);
        if (!m) return {};

        const [start, end] = i.split(":");
        return {
          start: new Date(start + "-01").getTime(),
          end: new Date(
            new Date(end + "-01").getFullYear(),
            new Date(end + "-01").getMonth() + 1,
            0,
          ).getTime(),
        };
      })
      .filter((i) => !isNaN(i.start) && !isNaN(i.end)) ?? [];

  interruptions.sort((a, b) => a.start - b.start);

  const styleProps = {
    y: isReal ? y - REAL_Y_OFFSET : y,
    color: isReal ? color : Highcharts.color(color).setOpacity(0.4).get(),
  };

  let currentStart = startDate;
  interruptions.forEach(({ start, end }) => {
    if (currentStart < start) {
      segments.push({
        name: activity.name,
        start: currentStart,
        end: start - 1,
        ...styleProps,
      });
    }
    currentStart = end;
  });

  if (currentStart < endDate) {
    segments.push({
      name: activity.name,
      start: currentStart,
      end: endDate,
      ...styleProps,
    });
  }

  return segments;
};

const getSeries = (activities, tasks) => {
  let y = 0;
  const previsto = [];
  const real = [];

  activities.forEach((act) => {
    const previstoAct = processActivity(act, y, "#44505dff", false);
    const realAct = processActivity(act, y, "#003366", true);
    previsto.push(...previstoAct);
    real.push(...realAct);
    y++;

    const relatedTasks = tasks.filter((t) => t.parent === act.name);
    relatedTasks.forEach((task) => {
      const previstoTask = processActivity(task, y, "#5caafeff", false);
      const realTask = processActivity(task, y, "#007bff", true);
      previsto.push(...previstoTask);
      real.push(...realTask);
      y++;
    });
  });

  return { previsto, real };
};

let allowChartUpdate = true;

export const getGanttOptions = (startDate, endDate, executedDate, gantt) => {
  const activities = gantt.filter((act) => !act.parent);
  const tasks = gantt.filter((act) => act.parent);

  const categories = activities.flatMap((act) => [
    act.name,
    ...tasks.filter((t) => t.parent === act.name).map((t) => t.name),
  ]);

  const actIndex = [];
  let index = 0;

  for (const act of activities) {
    actIndex.push(index); // WP sempre aparece primeiro no flatMap
    const taskCount = tasks.filter((t) => t.parent === act.name).length;
    index += 1 + taskCount; // pula WP + tarefas
  }

  const { previsto, real } = getSeries(activities, tasks);

  const executionDateEndOfMonth = executedDate
    ? getLastDayOfMonth(executedDate)
    : null;

  // Exporting Utils
  const PX_PER_MONTH = 10; // largura por mês no PDF
  const EXTRA_HEADER = 300; // espaço para título/legend/eixos

  function monthSpan(minMS, maxMS) {
    const a = new Date(minMS),
      b = new Date(maxMS);
    return (
      (b.getUTCFullYear() - a.getUTCFullYear()) * 12 +
      (b.getUTCMonth() - a.getUTCMonth()) +
      1
    );
  }

  return {
    chart: {
      type: "gantt",
      height: 500,
      scrollablePlotArea: {
        minHeight: 500,
        scrollPositionX: 1,
        opacity: 1,
      },
      events: {
        render: function () {
          if (allowChartUpdate) {
            allowChartUpdate = false;

            this.update({
              chart: {
                scrollablePlotArea: {
                  minHeight:
                    this.seriesGroup.getBBox(true).height + this.plotTop + 30,
                },
              },
            });

            try {
              const x = this.xAxis[0];
              const y = this.yAxis[0];

              const min = x.getExtremes().min;
              const max = x.getExtremes().max;
              const months = Math.max(1, monthSpan(min, max));

              const cats = y.categories || [];
              const fontSize = 12; // o mesmo que usas no browser

              // altura baseada no nº de tarefas e na tua staticScale (≈ 30 px por linha)
              const tasks = cats.length || 10;
              const sourceWidth = Math.max(1600, months * PX_PER_MONTH) + 700;
              const sourceHeight = Math.max(800, tasks * 30 + EXTRA_HEADER);

              this.update(
                {
                  exporting: {
                    // estes options aplicam-se SÓ ao ficheiro exportado
                    chartOptions: {
                      chart: {
                        spacingTop: 5,
                        width: sourceWidth,
                        height: sourceHeight,
                        events: {
                          render: function () {
                            const chart = this;
                            const axis = chart.yAxis[0];

                            // recriar o grupo a cada render/redraw
                            if (chart.wpLabelBg) chart.wpLabelBg.destroy();
                            const grp = (chart.wpLabelBg = chart.renderer
                              .g("wp-label-bg")
                              .attr({ zIndex: 1 }) // atrás dos labels (labels têm zIndex alto)
                              .add());

                            const leftX = 0; // início total do chart
                            const width = chart.plotLeft; // largura da zona dos labels + eixo

                            // retângulo por linha WP
                            actIndex.forEach((i) => {
                              // limites em px da linha i (meia célula para cima/baixo)
                              let yTop = axis.toPixels(i - 0.5);
                              let yBot = axis.toPixels(i + 0.5);

                              // clamp ao plot area para evitar artefactos
                              const top = Math.max(yTop, chart.plotTop);
                              const bot = Math.min(
                                yBot,
                                chart.plotTop + chart.plotHeight,
                              );
                              const h = Math.max(0, bot - top);

                              if (h > 0) {
                                chart.renderer
                                  .rect(leftX, top, width, h, 0)
                                  .attr({ fill: "#f2f2f2", stroke: "none" })
                                  .add(grp);
                              }
                            });
                          },
                        },
                      },
                      // rótulos em SVG puro (sem ellipsis) e com medição automática
                      yAxis: {
                        type: "category",
                        plotBands: actIndex.map((i) => ({
                          from: i - 0.5,
                          to: i + 0.5,
                          color: "#f2f2f2", // cinzento claro
                          zIndex: 0,
                        })),
                        labels: {
                          formatter: function () {
                            const isWp = actIndex.includes(this.pos);
                            const text = String(this.value);
                            // IMPORTANTE: width:100% + display:block para o fundo ocupar toda a largura reservada
                            const base = "display:block;width:100%;";
                            const font = isWp
                              ? "font-weight:700;"
                              : "font-weight:400;";
                            // pode adicionar padding para o fundo “respirar”
                            const pad = "padding:0 6px;box-sizing:border-box;";
                            return `<div style="${base}${font}${pad}">${text}</div>`;
                          },
                          useHTML: true,
                          align: "left",
                          reserveSpace: true,
                          style: {
                            textAlign: "left",
                            fontSize: `${fontSize}px`, // Font size for the labels
                            width: "500px", // Maximum width of the label
                            whiteSpace: "nowrap", // Prevent wrapping, add ellipsis if necessary
                            overflow: "hidden", // Hide the overflow
                            textOverflow: "ellipsis", // Add ellipsis if the text exceeds the width
                            display: "block",
                          },
                        },
                      },
                      xAxis: [
                        {
                          min,
                          max,
                        },
                        { linkedTo: 0 }, // anos
                      ],
                    },
                    fallbackToExportServer: false,
                  },
                },
                false,
              );
            } finally {
              allowChartUpdate = true;
            }
          }
        },
      },
    },
    exporting: {
      enabled: true,
      fallbackToExportServer: false,
      buttons: {
        contextButton: {
          menuItems: ["downloadPNG", "downloadPDF"],
        },
      },
    },
    legend: {
      enabled: true,
    },
    colorAxis: {
      dataClasses: [
        { from: 1, to: 1, color: "#007bff", name: "Em curso (Tarefa)" },
        { from: 0, to: 0, color: "#5caafeff", name: "Planeado (Tarefa)" },
        { from: 3, to: 3, color: "#003366", name: "Em curso (Atividade)" },
        { from: 2, to: 2, color: "#44505dff", name: "Planeado (Atividade)" },
      ],
    },
    xAxis: [
      {
        labels: {
          format: "{value:%b}",
          style: {
            fontSize: "12px",
          },
        },
        min: startDate ? new Date(startDate).getTime() : null,
        max: endDate ? new Date(endDate).getTime() : null,
        units: [["month", [1]]],
        gridLineWidth: 1,
      },
      {
        linkedTo: 0,
        labels: {
          format: "{value:%Y}",
          style: {
            fontSize: "16px",
          },
        },
        units: [["year", [1]]],
        lineWidth: 3,
        gridLineWidth: 3,
        plotBands: executedDate
          ? [
              {
                from: new Date(startDate).getTime(),
                to: executionDateEndOfMonth,
                color: "rgba(0,0,0,0.07)",
                zIndex: 0,
              },
            ]
          : [],
        plotLines: executedDate
          ? [
              {
                value: executionDateEndOfMonth,
                color: "#003366",
                width: 2,
                dashStyle: "Dash",
                label: {
                  text: "Execução Atual",
                  align: "left",
                  verticalAlign: "bottom",
                  rotation: 0,
                  style: {
                    color: "black",
                    fontWeight: "bold",
                  },
                },
                zIndex: 5,
              },
            ]
          : [],
      },
    ],
    yAxis: {
      grid: {
        borderWidth: 0,
      },
      gridLineWidth: 0,
      labels: {
        formatter: function () {
          return actIndex.includes(this.pos)
            ? `<span style="font-weight:bold">${this.value}</span>`
            : this.value;
        },
        useHTML: true,
        style: {
          fontSize: "12px", // Font size for the labels
          width: "150px", // Maximum width of the label
          whiteSpace: "nowrap", // Prevent wrapping, add ellipsis if necessary
          overflow: "hidden", // Hide the overflow
          textOverflow: "ellipsis", // Add ellipsis if the text exceeds the width
        },
      },
      type: "category",
      categories: categories,
      staticScale: 30,
    },
    plotOptions: {
      series: {
        enableMouseTracking: false,
      },
    },
    series: [
      {
        id: "previsto",
        name: "Previsto",
        data: previsto,
        dataLabels: { enabled: false },
        pointWidth: PREVISTO_POINT_WIDTH,
        borderWidth: 0,
      },
      {
        id: "real",
        name: "Real",
        data: real,
        dataLabels: { enabled: false },
        pointWidth: REAL_POINT_WIDTH,
        borderWidth: 0,
      },
    ],
    credits: {
      enabled: false,
    },
  };
};

export const getWorkingDays = (start, end) => {
  let current = new Date(start);
  let endDate = new Date(end);
  let result = [];

  while (current <= endDate) {
    let year = current.getFullYear();
    let month = current.getMonth() + 1;
    let daysInMonth = new Date(year, month, 0).getDate();
    let workingDays = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      let date = new Date(year, month - 1, day);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        workingDays++;
      }
    }

    result.push(workingDays);
    current.setMonth(current.getMonth() + 1);
  }
  return result;
};

export const getMonthsMap = (start, end) => {
  let monthsMap = new Map();
  let current = new Date(start);
  let endDate = new Date(end);
  let index = 0;
  while (current <= endDate) {
    let key = `${current.getFullYear()}-${(current.getMonth() + 1)
      .toString()
      .padStart(2, "0")}`;
    monthsMap.set(key, index++);
    current.setMonth(current.getMonth() + 1);
  }
  return monthsMap;
};

export const applyConstrainedAllocation = (rates, caps, total) => {
  const keys = Object.keys(rates);
  const allocation = {};
  let remaining = Number(total);

  // Força que rates e caps sejam números válidos
  const cleanRates = {};
  const cleanCaps = {};

  for (const key of keys) {
    const rawRate = rates[key];
    const rawCap = caps[key];

    const rate = Math.max(0, Number(rawRate) || 0); // default 0
    const cap = Math.min(100, Math.max(rate, Number(rawCap) || 100)); // mínimo igual ao rate, máx 100

    cleanRates[key] = rate;
    cleanCaps[key] = cap;
  }

  // Aplica mínimos (rates)
  for (const key of keys) {
    allocation[key] = cleanRates[key];
    remaining -= cleanRates[key];
  }

  if (remaining < 0) {
    // Os mínimos já excedem o total → normaliza proporcionalmente
    const minSum = keys.reduce((sum, key) => sum + cleanRates[key], 0);
    return keys.reduce((acc, key) => {
      acc[key] = (cleanRates[key] / minSum) * total;
      return acc;
    }, {});
  }

  // Distribuir restante respeitando os caps
  let freeKeys = keys.filter((key) => cleanCaps[key] > allocation[key]);

  while (remaining > 0.001 && freeKeys.length > 0) {
    const portion = remaining / freeKeys.length;
    const nextFree = [];

    for (const key of freeKeys) {
      const cap = cleanCaps[key];
      const available = cap - allocation[key];

      if (available >= portion) {
        allocation[key] += portion;
        remaining -= portion;
        nextFree.push(key);
      } else {
        allocation[key] += available;
        remaining -= available;
      }
    }

    freeKeys = nextFree;
  }

  return allocation;
};

// Normaliza "YYYY-MM" ou "YYYY-MM-DD" -> "YYYY-MM"
function normalizeYm(input) {
  if (!input) return null;
  const m = String(input).match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : null;
}

export const generateDateRange = (start, end, range = null) => {
  const s = normalizeYm(start);
  const e = normalizeYm(end);
  if (!s || !e) return [];

  let [sy, sm] = s.split("-").map(Number);
  let [ey, em] = e.split("-").map(Number);

  const clampFrom = normalizeYm(range?.from) || s;
  const clampTo = normalizeYm(range?.to) || e;

  const out = [];
  // loop inclusive: while ym <= end
  while (sy < ey || (sy === ey && sm <= em)) {
    const ym = `${sy}-${String(sm).padStart(2, "0")}`;
    if (ym >= clampFrom && ym <= clampTo) out.push(ym);

    sm += 1;
    if (sm === 13) {
      sm = 1;
      sy += 1;
    }
  }
  return out;
};

export const generateHiddenColumns = (
  fullRange,
  filteredRange,
  fixedColumns,
) => {
  const columns = [];
  let filterInd = 0;

  fullRange.forEach((month, ind) => {
    if (month !== filteredRange?.[filterInd]) {
      columns.push(ind + fixedColumns);
    } else {
      filterInd++;
    }
  });

  return {
    columns,
  };
};

export const generateEditableMap = (dateRange, activities) => {
  if (!dateRange || !activities) return [];

  const isInInterruption = (month, interruptions) => {
    if (!interruptions) return false;

    return interruptions
      .split(";")
      .filter(Boolean)
      .some((period) => {
        const [start, end] = period.split(":");
        return month >= start && month <= end;
      });
  };

  return activities.map((act) => {
    const { name, start, end, interruptions } = act;

    return [
      name,
      ...dateRange.map((date) => {
        const lastDayOfMonth = date + "-31";
        const isWithinActivity = lastDayOfMonth >= start && date <= end;

        const isInInterrupt = isInInterruption(date, interruptions); // check mid-month

        return isWithinActivity && !isInInterrupt;
      }),
    ];
  });
};

export const updateActivitesDates = (tasks, activities) => {
  const groupByActivity = {};

  tasks.forEach(
    ({
      parent,
      start_date,
      end_date,
      interruptions,
      real_start_date,
      real_end_date,
      real_interruptions,
    }) => {
      const start = start_date ? new Date(start_date) : null;
      const end = end_date ? new Date(end_date) : null;
      const realStart = real_start_date ? new Date(real_start_date) : null;
      const realEnd = real_end_date ? new Date(real_end_date) : null;

      // Ignora tarefas sem pai
      if (!parent) return;

      if (!groupByActivity[parent]) groupByActivity[parent] = [];

      groupByActivity[parent].push({
        start,
        end,
        interruptions: interruptions || "",
        realStart,
        realEnd,
        realInterruptions: real_interruptions || "",
      });
    },
  );

  const activitiesData = activities.map((act) => {
    const actTasks = groupByActivity[act.name];
    if (!actTasks || actTasks.length === 0) {
      return {
        ...act,
        start_date: "",
        end_date: "",
        interruptions: "",
        real_start_date: "",
        real_end_date: "",
        real_interruptions: "",
      };
    }

    // Define o início e fim do WP com base nas atividades
    // let minStart = new Date(Math.min(...wpActs.map(act => act.start.getTime())));
    // let maxEnd = new Date(Math.max(...wpActs.map(act => act.end.getTime())));
    let minStart = actTasks.some((task) => task.start)
      ? new Date(
          Math.min(
            ...actTasks.filter((t) => t.start).map((t) => t.start.getTime()),
          ),
        )
      : null;

    let maxEnd = actTasks.some((task) => task.end)
      ? new Date(
          Math.max(
            ...actTasks.filter((t) => t.end).map((t) => t.end.getTime()),
          ),
        )
      : null;

    let minRealStart = actTasks.some((task) => task.realStart)
      ? new Date(
          Math.min(
            ...actTasks
              .filter((t) => t.realStart)
              .map((t) => t.realStart.getTime()),
          ),
        )
      : null;

    let maxRealEnd = actTasks.some((task) => task.realEnd)
      ? new Date(
          Math.max(
            ...actTasks
              .filter((t) => t.realEnd)
              .map((t) => t.realEnd.getTime()),
          ),
        )
      : null;

    // Coletar todos os meses ativos de todas as atividades
    const activeMonths = new Set();
    const realActiveMonths = new Set();

    actTasks.forEach(
      ({
        start,
        end,
        interruptions,
        realStart,
        realEnd,
        realInterruptions,
      }) => {
        const months = [];
        const realMonths = [];

        if (start && end) {
          for (
            let d = new Date(start);
            d <= end;
            d.setMonth(d.getMonth() + 1)
          ) {
            months.push(
              `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
            );
          }
        }

        if (realStart && realEnd) {
          for (
            let d = new Date(realStart);
            d <= realEnd;
            d.setMonth(d.getMonth() + 1)
          ) {
            realMonths.push(
              `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
            );
          }
        }

        const skip = new Set();
        const realSkip = new Set();
        const INTERVAL_RE = /^\s*(\d{4})-(\d{2})\s*:\s*(\d{4})-(\d{2})\s*$/;

        if (interruptions?.trim()) {
          interruptions
            .split(";")
            .filter(Boolean)
            .forEach((interval) => {
              const m = INTERVAL_RE.exec(interval);
              if (!m) return;

              const [from, to] = interval.split(":");
              getMonthRange(from, to).forEach((m) => skip.add(m));
            });
        }

        if (realInterruptions?.trim()) {
          realInterruptions
            .split(";")
            .filter(Boolean)
            .forEach((interval) => {
              const m = INTERVAL_RE.exec(interval);
              if (!m) return;

              const [from, to] = interval.split(":");
              getMonthRange(from, to).forEach((m) => realSkip.add(m));
            });
        }

        months.forEach((month) => {
          if (!skip.has(month)) {
            activeMonths.add(month);
          }
        });

        realMonths.forEach((month) => {
          if (!realSkip.has(month)) {
            realActiveMonths.add(month);
          }
        });
      },
    );

    // Gera todos os meses do período total do WP
    const fullRange = [];
    const realFullRange = [];

    if (minStart && maxEnd) {
      for (
        let d = new Date(minStart);
        d <= maxEnd;
        d.setMonth(d.getMonth() + 1)
      ) {
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0",
        )}`;
        if (!activeMonths.has(ym)) {
          fullRange.push(ym);
        }
      }
    }

    if (minRealStart && maxRealEnd) {
      for (
        let d = new Date(minRealStart);
        d <= maxRealEnd;
        d.setMonth(d.getMonth() + 1)
      ) {
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0",
        )}`;
        if (!realActiveMonths.has(ym)) {
          realFullRange.push(ym);
        }
      }
    }

    // Agrupa meses consecutivos
    const interruptionIntervals = [];
    const realInterruptionIntervals = [];
    let currentStart = null;
    let prev = null;

    fullRange.forEach((m) => {
      if (!currentStart) {
        currentStart = m;
        prev = m;
      } else {
        const [prevY, prevM] = prev.split("-").map(Number);
        const [curY, curM] = m.split("-").map(Number);

        const prevDate = new Date(prevY, prevM - 1);
        const curDate = new Date(curY, curM - 1);

        const diff =
          (curDate.getFullYear() - prevDate.getFullYear()) * 12 +
          (curDate.getMonth() - prevDate.getMonth());

        if (diff === 1) {
          prev = m;
        } else {
          interruptionIntervals.push(`${currentStart}:${prev}`);
          currentStart = m;
          prev = m;
        }
      }
    });

    if (currentStart) {
      interruptionIntervals.push(`${currentStart}:${prev}`);
    }

    currentStart = null;
    prev = null;

    realFullRange.forEach((m) => {
      if (!currentStart) {
        currentStart = m;
        prev = m;
      } else {
        const [prevY, prevM] = prev.split("-").map(Number);
        const [curY, curM] = m.split("-").map(Number);

        const prevDate = new Date(prevY, prevM - 1);
        const curDate = new Date(curY, curM - 1);

        const diff =
          (curDate.getFullYear() - prevDate.getFullYear()) * 12 +
          (curDate.getMonth() - prevDate.getMonth());

        if (diff === 1) {
          prev = m;
        } else {
          realInterruptionIntervals.push(`${currentStart}:${prev}`);
          currentStart = m;
          prev = m;
        }
      }
    });

    if (currentStart) {
      realInterruptionIntervals.push(`${currentStart}:${prev}`);
    }

    return {
      ...act,
      start_date: minStart ? minStart.toISOString().split("T")[0] : "",
      end_date: maxEnd ? maxEnd.toISOString().split("T")[0] : "",
      interruptions:
        interruptionIntervals.length > 0
          ? interruptionIntervals.join(";") + ";"
          : "",
      real_start_date: minRealStart
        ? minRealStart.toISOString().split("T")[0]
        : "",
      real_end_date: maxRealEnd ? maxRealEnd.toISOString().split("T")[0] : "",
      real_interruptions:
        realInterruptionIntervals.length > 0
          ? realInterruptionIntervals.join(";") + ";"
          : "",
    };
  });

  return activitiesData;
};

export const validateNumber = (value) =>
  value === "" || value === null || value === undefined || isNaN(Number(value))
    ? 0
    : Number(value);

function getMonthRange(from, to) {
  const [y0, m0] = from.split("-").map(Number);
  const [y1, m1] = to.split("-").map(Number);
  const months = [];
  let year = y0,
    month = m0;
  while (year < y1 || (year === y1 && month <= m1)) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month === 13) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

function getLastDayOfMonth(dateStr) {
  const date = new Date(dateStr);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime(); // último dia do mês
}
